/**
 * Maps technical API validation messages to user-friendly text.
 * Handles patterns like "branchId: Required" → "Please select Branch".
 */

const FIELD_LABELS: Record<string, string> = {
  companyId: 'Company',
  branchId: 'Branch',
  warehouseId: 'Warehouse',
  productId: 'Product',
  customerId: 'Customer',
  supplierId: 'Supplier',
  invoiceId: 'Invoice',
  purchaseOrderId: 'Purchase Order',
  roleId: 'Role',
  userId: 'User',
  fromWarehouseId: 'Source Warehouse',
  toWarehouseId: 'Destination Warehouse',
  sourceBranchId: 'Source Branch',
  destinationBranchId: 'Destination Branch',
  placeOfSupplyStateCode: 'Place of Supply (State Code)',
  invoiceDate: 'Invoice Date',
  dueDate: 'Due Date',
  paymentTerms: 'Payment Terms',
  paymentDate: 'Payment Date',
  paymentMethod: 'Payment Method',
  customerName: 'Customer Name',
  customerEmail: 'Customer Email',
  customerPhone: 'Customer Phone',
  customerAddress: 'Customer Address',
  customerCity: 'Customer City',
  customerState: 'Customer State',
  customerZipCode: 'Customer Zip Code',
  supplierName: 'Supplier Name',
  supplierEmail: 'Supplier Email',
  supplierPhone: 'Supplier Phone',
  supplierAddress: 'Supplier Address',
  productName: 'Product Name',
  quantity: 'Quantity',
  quantityDelta: 'Quantity',
  receivedQuantity: 'Received Quantity',
  orderedQuantity: 'Ordered Quantity',
  unitPrice: 'Unit Price',
  costPrice: 'Cost Price',
  price: 'Price',
  amount: 'Amount',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  code: 'Code',
  sku: 'SKU',
  hsn: 'HSN',
  sac: 'SAC',
  gstRate: 'GST Rate',
  gstin: 'GSTIN',
  status: 'Status',
  items: 'Items',
  notes: 'Notes',
  description: 'Description',
  location: 'Location',
  password: 'Password',
  currency: 'Currency',
  batchNumber: 'Batch Number',
  billNumber: 'Bill Number',
  supplier: 'Supplier',
  receivingDate: 'Receiving Date',
  orderDate: 'Order Date',
  expectedDeliveryDate: 'Expected Delivery Date',
};

const SELECT_FIELDS = new Set([
  'companyId',
  'branchId',
  'warehouseId',
  'productId',
  'customerId',
  'supplierId',
  'invoiceId',
  'purchaseOrderId',
  'roleId',
  'userId',
  'fromWarehouseId',
  'toWarehouseId',
  'sourceBranchId',
  'destinationBranchId',
  'paymentMethod',
  'status',
  'currency',
]);

function humanizeFieldKey(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const withoutId = key.replace(/Id$/, '');
  const spaced = withoutId
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function requiredMessage(field: string): string {
  const label = humanizeFieldKey(field);
  if (SELECT_FIELDS.has(field) || /Id$/.test(field)) {
    return `Please select ${label}`;
  }
  if (field === 'items') {
    return 'Please add at least one item';
  }
  if (field === 'email') {
    return 'Please enter a valid Email';
  }
  return `Please enter ${label}`;
}

function isGenericRequiredMessage(message: string): boolean {
  const trimmed = message.trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  return (
    lower === 'required' ||
    lower.includes('string must contain at least') ||
    lower.includes('expected string') ||
    lower.includes('expected number') ||
    /^(?:[a-z0-9_.]+\s+)?is required(?:\s+for\b.*)?$/i.test(trimmed)
  );
}

/**
 * Rewrite a single segment such as "branchId: Required" or "branchId is required".
 */
function humanizeSingleValidationSegment(segment: string): string {
  const trimmed = segment.trim();
  if (!trimmed) return trimmed;

  // Already friendly (no technical Id keys)
  if (!/\b\w+Id\b/.test(trimmed) && !/^[a-z][a-zA-Z0-9_.]*:\s*/.test(trimmed)) {
    return trimmed;
  }

  // "path.to.field: message" or "field: message"
  const colonMatch = trimmed.match(/^([a-zA-Z0-9_.]+)\s*:\s*(.+)$/);
  if (colonMatch) {
    const path = colonMatch[1];
    const message = colonMatch[2];
    const leaf = path.includes('.') ? path.split('.').filter((p) => !/^\d+$/.test(p)).pop() || path : path;
    if (isGenericRequiredMessage(message) || /is required/i.test(message)) {
      return requiredMessage(leaf);
    }
    return `${humanizeFieldKey(leaf)}: ${message}`;
  }

  // "branchId is required..." 
  const requiredMatch = trimmed.match(/^([a-zA-Z0-9_.]+)\s+is required(?:\s+for\b.*)?$/i);
  if (requiredMatch) {
    const leaf = requiredMatch[1].includes('.')
      ? requiredMatch[1].split('.').filter((p) => !/^\d+$/.test(p)).pop() || requiredMatch[1]
      : requiredMatch[1];
    return requiredMessage(leaf);
  }

  // Replace embedded technical keys
  return trimmed.replace(/\b([a-zA-Z][a-zA-Z0-9]*Id)\b/g, (_, key: string) => humanizeFieldKey(key));
}

/**
 * Humanize API / Zod validation error text for display in create/edit forms.
 */
export function humanizeValidationErrorMessage(raw: string | null | undefined): string {
  if (!raw || typeof raw !== 'string') return raw || '';
  const parts = raw.split(/\s*;\s*/).map((part) => humanizeSingleValidationSegment(part));
  return parts.filter(Boolean).join('; ');
}
